/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useReducer, useRef, useState} from "react"
import {get, head, isEmpty, uniq, trim} from "lodash"
import {useHistory, useLocation} from "react-router-dom"
import {v4 as uuid} from "uuid"
import cx from "classnames"
import {CreatedInfo} from "components/ui"
import {handleError, loadImage, toastError, toastSuccess} from "helpers"
import {
    DigitalDocumentFieldSetUp,
    TaskSubtype,
    UserType,
    DefaultDataType,
    TaskType,
    SignatureMethod,
    TaskStatus,
    DigitalTaskCondition
} from "types/tasks"
import {taskService} from "services"
import {DATA_TYPES, DEFAULT_DATA_TYPES, CHECKBOX_TYPES, INITIAL_TYPES, GRADE_DATA_TYPES} from "sections/Tasks/data"
import {DefinedUser, HeaderBody, FieldAction, FieldAlignActions} from "./parts"
import {alignFieldPositions, getFieldIdsInRect, getMovingFieldIds} from "./parts/utils/fieldLayout"
import styles from "./DigitalDocSetUp.module.css"
import {BaseLoading} from "components"
import * as queryString from "query-string"
import {duplicateField, getNextFieldIndex} from "./parts/utils/fields-duplication"
import {useTasks} from "sections/Tasks/hooks/useTasks"
import {useDigitalSetup} from "./useDigitalSetup"
import {useSubmit} from "./useSubmit"
import {useDigitalField} from "./useDigitalField"

function reducer(state, action) {
    return {...state, ...action}
}

export const SIGNATURE_METHOD_OPTIONS = [
    {id: SignatureMethod.HAND, name: "Sign by hand"},
    {id: SignatureMethod.KEYBOARD, name: "Sign by keyboard"},
    {id: SignatureMethod.BOTH, name: "Allow both signing methods"}
]

type Props = {
    taskData: any
    isCreation: boolean
    onCloseDigitalDocSetup: () => void
}

export function DigitalDocSetUp(props: Props) {
    const {taskData, isCreation, onCloseDigitalDocSetup} = props
    const [task, setTask] = useState<any>(null)
    const bodyRef = useRef(null)
    const wrapRef = useRef(null)
    const pageRef = useRef(null)
    const initialState = {
        fields: [],
        activeField: null,
        fieldBodyContent: null,
        isSaving: false,
        isImgsLoaded: false,
        definedUserTypes: [
            {id: UserType.STUDENT, name: "Student"},
            {id: UserType.STAFF, name: "Staff"},
            {id: UserType.OTHERS, name: "Others"}
        ],
        participants: [],
        definedUsers: [],
        combinedColor: {},
        scaleInfo: {},
        previousFieldInfo: {width: 32, height: 32, signatureTypes: []},
        selectedFieldIds: [],
        groupDrag: null
    }
    const [
        {
            fields,
            activeField,
            fieldBodyContent,
            isSaving,
            isImgsLoaded,
            definedUserTypes,
            definedUsers,
            participants,
            combinedColor,
            scaleInfo,
            previousFieldInfo,
            selectedFieldIds,
            groupDrag
        },
        dispatch
    ] = useReducer(reducer, initialState)
    const fieldsRef = useRef(fields)
    const selectedFieldIdsRef = useRef<string[]>(selectedFieldIds)
    const activeFieldRef = useRef(activeField)
    const boundaryRef = useRef({width: 0, height: 0})
    const dragSessionRef = useRef(null)
    const areaSelectRef = useRef(null)
    const areaSelectListenersRef = useRef<{
        move: ((event: MouseEvent) => void) | null
        up: ((event: MouseEvent) => void) | null
    }>({
        move: null,
        up: null
    })
    const isEditableRef = useRef(false)
    const keyHandlerRef = useRef<(event: KeyboardEvent) => void>(() => {})
    fieldsRef.current = fields
    selectedFieldIdsRef.current = selectedFieldIds
    activeFieldRef.current = activeField
    const history = useHistory()
    const location = useLocation()
    const queryParams = queryString.parse(location.search)
    const [errorFieldIds, setErrorFieldIds] = useState(null)
    const [selectionBox, setSelectionBox] = useState(null)
    const margin = 10
    const {getBackUrlTab} = useTasks()
    const {getIconByType} = useDigitalSetup()
    const {getDataSubmit} = useSubmit()
    const {initFields, getCircularDependencyFields} = useDigitalField()
    const isDraftTask = taskData.isDraft || taskData.status === TaskStatus.DRAFT
    const isEditable =
        !Boolean(taskData.deletedAt) &&
        (isDraftTask ||
            isCreation ||
            ([TaskType.DIGITAL_DOCUMENT, TaskType.CUSTOM_DD].includes(taskData.type) &&
                taskData.subtype === TaskSubtype.LIBRARY))
    isEditableRef.current = isEditable

    useEffect(() => {
        initData()
    }, [])

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => keyHandlerRef.current(event)
        document.addEventListener("keydown", onKeyDown)
        return () => document.removeEventListener("keydown", onKeyDown)
    }, [])

    useEffect(() => {
        return () => {
            const {move, up} = areaSelectListenersRef.current
            if (move) document.removeEventListener("mousemove", move)
            if (up) document.removeEventListener("mouseup", up)
        }
    }, [])

    const initData = async () => {
        let task = taskData
        try {
            if (taskData.type === TaskType.CUSTOM_DD) {
                task = await taskService.getCustomDigitalTask(taskData.id)
            } else {
                task = await taskService.getOne(taskData.id)
            }
            setTask(task)
            await Promise.all(task.digitalDocument.pages.map((image) => loadImage(image)))
        } catch (error) {
            console.log("Failed to load images", error)
        } finally {
            dispatch({isImgsLoaded: true})
        }
        const {allFields, newDefinedUsers, newParticipants, newDefinedUserTypes, scaleInfo} = initFields({
            task,
            definedUserTypes,
            pageRef,
            margin
        })
        dispatch({
            fields: allFields,
            definedUsers: newDefinedUsers,
            participants: newParticipants,
            definedUserTypes: newDefinedUserTypes,
            scaleInfo
        })
    }

    const onDragStart = (_, data, draggedField) => {
        const movingIds = getMovingFieldIds(draggedField.id, selectedFieldIdsRef.current)
        const movingIdSet = new Set(movingIds)
        const starts = {}
        fieldsRef.current.forEach((item) => {
            if (!movingIdSet.has(item.id)) return
            starts[item.id] = {
                left: item.id === draggedField.id ? data.x : item.placeInformation.left || 0,
                top: item.id === draggedField.id ? data.y : item.placeInformation.top || 0
            }
        })
        dragSessionRef.current = {fieldId: draggedField.id, startX: data.x, startY: data.y, dx: 0, dy: 0, starts}
    }

    const onDrag = (_, data, draggedField) => {
        const session = dragSessionRef.current
        if (!session || session.fieldId !== draggedField.id) return
        const dx = data.x - session.startX
        const dy = data.y - session.startY
        if (session.dx === dx && session.dy === dy) return
        session.dx = dx
        session.dy = dy
        dispatch({groupDrag: {dx, dy, starts: session.starts}})
    }

    const onDragStop = (_, data, draggedField, didDrag = true) => {
        const session = dragSessionRef.current
        dragSessionRef.current = null
        if (!didDrag || !session || session.fieldId !== draggedField.id) {
            if (groupDrag) {
                dispatch({groupDrag: null})
            }
            return
        }
        const dx = data.x - session.startX
        const dy = data.y - session.startY
        const movingIds = Object.keys(session.starts)
        if (dx === 0 && dy === 0) {
            dispatch({groupDrag: null})
            return
        }
        const newFields = fieldsRef.current.map((item) => {
            const start = session.starts[item.id]
            if (!start) return item
            return {
                ...item,
                placeInformation: {...item.placeInformation, left: start.left + dx, top: start.top + dy}
            }
        })
        fieldsRef.current = newFields
        const nextSelectedIds = movingIds.length > 1 ? selectedFieldIdsRef.current : [draggedField.id]
        const currentActiveField = activeFieldRef.current
        const activeStillSelected = currentActiveField && nextSelectedIds.includes(currentActiveField.id)
        dispatch({
            fields: newFields,
            groupDrag: null,
            selectedFieldIds: nextSelectedIds,
            activeField: activeStillSelected ? currentActiveField : null
        })
    }

    const moveSelectedFields = (dx, dy) => {
        const selectedIds = selectedFieldIdsRef.current
        if (!selectedIds.length) return
        const selectedIdSet = new Set(selectedIds)
        const {width, height} = boundaryRef.current
        let minDx = -Infinity
        let maxDx = Infinity
        let minDy = -Infinity
        let maxDy = Infinity
        fieldsRef.current.forEach((item) => {
            if (!selectedIdSet.has(item.id)) return
            const left = item.placeInformation.left || 0
            const top = item.placeInformation.top || 0
            minDx = Math.max(minDx, -left)
            maxDx = Math.min(maxDx, width - left)
            minDy = Math.max(minDy, -top)
            maxDy = Math.min(maxDy, height - top)
        })
        const nextDx = Math.min(Math.max(dx, minDx), maxDx)
        const nextDy = Math.min(Math.max(dy, minDy), maxDy)
        if (nextDx === 0 && nextDy === 0) return
        const newFields = fieldsRef.current.map((item) => {
            if (!selectedIdSet.has(item.id)) return item
            return {
                ...item,
                placeInformation: {
                    ...item.placeInformation,
                    left: (item.placeInformation.left || 0) + nextDx,
                    top: (item.placeInformation.top || 0) + nextDy
                }
            }
        })
        fieldsRef.current = newFields
        dispatch({fields: newFields, groupDrag: null})
    }

    const onToggleFieldSelection = (field) => {
        const currentIds = selectedFieldIdsRef.current
        const nextIds = currentIds.includes(field.id)
            ? currentIds.filter((id) => id !== field.id)
            : [...currentIds, field.id]
        const nextActiveField =
            nextIds.length === 1 ? fieldsRef.current.find((item) => item.id === nextIds[0]) || null : null
        dispatch({selectedFieldIds: nextIds, activeField: nextActiveField, groupDrag: null})
    }

    const onSelectOnlyField = (field) => {
        dispatch({
            selectedFieldIds: field ? [field.id] : [],
            activeField: field,
            groupDrag: null
        })
    }

    const onAlignFields = (edge) => {
        const newFields = alignFieldPositions(fieldsRef.current, selectedFieldIdsRef.current, edge, boundaryRef.current)
        fieldsRef.current = newFields
        dispatch({fields: newFields, groupDrag: null})
    }

    const getPointInBody = (event: MouseEvent | React.MouseEvent) => {
        const body = bodyRef.current
        if (!body) return null
        const rect = body.getBoundingClientRect()
        return {
            x: event.clientX - rect.left,
            y: event.clientY - rect.top
        }
    }

    const clearAreaSelectListeners = () => {
        const {move, up} = areaSelectListenersRef.current
        if (move) document.removeEventListener("mousemove", move)
        if (up) document.removeEventListener("mouseup", up)
        areaSelectListenersRef.current = {move: null, up: null}
    }

    const onCanvasMouseDown = (event: React.MouseEvent) => {
        const target = event.target
        if (!(target instanceof Element)) return
        if (target.closest("[data-field-action], [data-field-toolbar]")) return
        if (event.button !== 0) return
        if (!isEditableRef.current) {
            if (!selectedFieldIdsRef.current.length && !activeFieldRef.current) return
            dispatch({selectedFieldIds: [], activeField: null, groupDrag: null})
            return
        }

        const point = getPointInBody(event)
        if (!point) return
        event.preventDefault()
        clearAreaSelectListeners()
        const additive = Boolean(event.shiftKey || event.metaKey || event.ctrlKey)
        areaSelectRef.current = {
            startX: point.x,
            startY: point.y,
            additive,
            baseSelectedIds: additive ? [...selectedFieldIdsRef.current] : []
        }
        setSelectionBox({left: point.x, top: point.y, width: 0, height: 0})
        if (!additive) {
            dispatch({selectedFieldIds: [], activeField: null, groupDrag: null})
        }

        const onAreaSelectMove = (moveEvent: MouseEvent) => {
            const session = areaSelectRef.current
            if (!session) return
            const movePoint = getPointInBody(moveEvent)
            if (!movePoint) return
            const left = Math.min(session.startX, movePoint.x)
            const top = Math.min(session.startY, movePoint.y)
            const width = Math.abs(movePoint.x - session.startX)
            const height = Math.abs(movePoint.y - session.startY)
            setSelectionBox({left, top, width, height})

            if (width <= 3 && height <= 3) return
            const idsInRect = getFieldIdsInRect(fieldsRef.current, {
                left: session.startX,
                top: session.startY,
                width: movePoint.x - session.startX,
                height: movePoint.y - session.startY
            })
            const nextIds = session.additive ? uniq([...session.baseSelectedIds, ...idsInRect]) : idsInRect
            const nextActiveField =
                nextIds.length === 1 ? fieldsRef.current.find((item) => item.id === nextIds[0]) || null : null
            dispatch({
                selectedFieldIds: nextIds,
                activeField: nextActiveField,
                groupDrag: null
            })
        }

        const onAreaSelectUp = (upEvent: MouseEvent) => {
            const session = areaSelectRef.current
            areaSelectRef.current = null
            clearAreaSelectListeners()
            setSelectionBox(null)
            if (!session) return

            const upPoint = getPointInBody(upEvent)
            if (!upPoint) return
            const didDrag = Math.abs(upPoint.x - session.startX) > 3 || Math.abs(upPoint.y - session.startY) > 3

            if (!didDrag) {
                if (!session.additive && (selectedFieldIdsRef.current.length || activeFieldRef.current)) {
                    dispatch({selectedFieldIds: [], activeField: null, groupDrag: null})
                }
                return
            }

            const idsInRect = getFieldIdsInRect(fieldsRef.current, {
                left: session.startX,
                top: session.startY,
                width: upPoint.x - session.startX,
                height: upPoint.y - session.startY
            })
            const nextIds = session.additive ? uniq([...session.baseSelectedIds, ...idsInRect]) : idsInRect
            const nextActiveField =
                nextIds.length === 1 ? fieldsRef.current.find((item) => item.id === nextIds[0]) || null : null
            dispatch({
                selectedFieldIds: nextIds,
                activeField: nextActiveField,
                fieldBodyContent: nextActiveField,
                groupDrag: null
            })
        }

        areaSelectListenersRef.current = {move: onAreaSelectMove, up: onAreaSelectUp}
        document.addEventListener("mousemove", onAreaSelectMove)
        document.addEventListener("mouseup", onAreaSelectUp)
    }

    const onResize = (_, {size}, newField) => {
        const newFields = fields.map((field) => {
            if (field.id === newField.id) {
                field.placeInformation = {...field.placeInformation, ...size}
            }
            return field
        })
        dispatch({fields: newFields, previousFieldInfo: {...previousFieldInfo, ...size}})
    }

    const setActiveField = (field) => {
        dispatch({activeField: field})
    }

    const onChangeField = (newField) => {
        const firstSignatureTypeId = get(newField, "signatureTypes[0].id", null)
        const newFields = fields.map((field) => {
            if (field.id === newField.id) {
                return {...field, ...newField}
            } else if (field.parentId === newField.id) {
                return {...field, parentSignatureTypeId: firstSignatureTypeId, signatureTypes: newField.signatureTypes}
            }
            return field
        })
        if (newField.signatureTypes && newField.signatureTypes.length > 0) {
            previousFieldInfo.signatureTypes = newField.signatureTypes
        }
        dispatch({fields: newFields, previousFieldInfo})
        return newFields
    }

    const onChangeFieldValue = (parentId, id, value) => {
        const newFields = fields.map((field) => {
            if (field.id === parentId) {
                field.fieldValues = field.fieldValues.map((fieldValue) => {
                    if (fieldValue.id === id) {
                        return {...fieldValue, ...value}
                    }
                    return fieldValue
                })
                return {...field}
            }
            return field
        })
        const fieldBodyContent = newFields.find((item) => item.id === parentId)
        dispatch({fields: newFields, fieldBodyContent})
    }

    const getSignatureTypes = (type) => {
        if (previousFieldInfo.signatureTypes && previousFieldInfo.signatureTypes.length) {
            return [DigitalDocumentFieldSetUp.SIGNATURE, DigitalDocumentFieldSetUp.INITIAL].includes(type)
                ? [head(previousFieldInfo.signatureTypes)]
                : previousFieldInfo.signatureTypes
        }
        const firstSigner = head(definedUsers)
        return firstSigner ? [firstSigner] : []
    }

    const onAddField = (type) => {
        const signatureTypes = getSignatureTypes(type)
        const nextFieldIndex = getNextFieldIndex(fields, type)
        const newField: any = {
            id: uuid(),
            type,
            label: "",
            uuid: uuid(),
            fieldIndex: nextFieldIndex,
            required: [DigitalDocumentFieldSetUp.SIGNATURE, DigitalDocumentFieldSetUp.INITIAL].includes(type),
            signatureTypes,
            defaultParticipant: head(participants),
            placeInformation: {
                width: previousFieldInfo.width,
                height: previousFieldInfo.height,
                top: wrapRef.current.scrollTop,
                left: 0
            }
        }
        switch (type) {
            case DigitalDocumentFieldSetUp.INITIAL:
                newField.initialType = head(INITIAL_TYPES)
                break
            case DigitalDocumentFieldSetUp.TEXT:
                newField.dataType = head(DATA_TYPES)
                newField.defaultDataType = head(DEFAULT_DATA_TYPES)
                break
            case DigitalDocumentFieldSetUp.CHECKBOX:
                newField.checkboxType = head(CHECKBOX_TYPES)
                break
            case DigitalDocumentFieldSetUp.GRADE:
                newField.dataType = head(GRADE_DATA_TYPES)
                break
            case DigitalDocumentFieldSetUp.FILE:
                newField.fileType = head(CHECKBOX_TYPES)
                break
            default:
                break
        }
        if (
            [
                DigitalDocumentFieldSetUp.INITIAL,
                DigitalDocumentFieldSetUp.CHECKBOX,
                DigitalDocumentFieldSetUp.SELECT
            ].includes(type)
        ) {
            newField.fieldValues = [
                {
                    id: uuid(),
                    type,
                    parentId: newField.id,
                    parentSignatureTypeId: get(newField, "signatureTypes[0].id", null),
                    label: "",
                    index: 1,
                    placeInformation: {...newField.placeInformation}
                }
            ]
        }
        fields.push(newField)
        dispatch({
            fields,
            activeField: newField,
            fieldBodyContent: newField,
            selectedFieldIds: [newField.id],
            groupDrag: null
        })
    }

    const onAddNewFieldValue = (parentId, fieldValue, newFieldsData = fields) => {
        if (fieldValue.type === DigitalDocumentFieldSetUp.DATE_FIELD) {
            newFieldsData.push(fieldValue)
            const fieldBodyContent = newFieldsData.find((item) => item.id === parentId)
            dispatch({fields: newFieldsData, fieldBodyContent})
        } else {
            const newFields = newFieldsData.map((field) => {
                if (field.id === parentId) {
                    field.fieldValues.push(fieldValue)
                    return {...field}
                }
                return field
            })
            if (fieldValue.type === DigitalDocumentFieldSetUp.SELECT) {
                dispatch({fields: newFields})
            } else {
                newFields.push(fieldValue)
                const fieldBodyContent = newFields.find((item) => item.id === parentId)
                dispatch({fields: newFields, fieldBodyContent})
            }
        }
    }

    const onRemoveFieldValue = (parentId, id, newFieldsData = fields) => {
        let newFields = []
        if (id === DigitalDocumentFieldSetUp.DATE_FIELD) {
            newFields = newFieldsData.filter((field) => {
                return !(field.parentId === parentId && field.type === DigitalDocumentFieldSetUp.DATE_FIELD)
            })
        } else {
            newFields = newFieldsData
                .map((field) => {
                    if (field.id === parentId) {
                        field.fieldValues = field.fieldValues.filter((fieldValue) => {
                            return fieldValue.id !== id
                        })
                        field.fieldValues.forEach((fieldValue, index) => {
                            fieldValue.index = index + 1
                        })
                        return {...field}
                    }
                    if (!isEmpty(field.dependencies) && field.dependencies.some((item) => item.value?.id === id)) {
                        field.isConditionalRule = false
                        field.dependencies = []
                    }
                    return field
                })
                .filter((field) => {
                    if (field.parentId === parentId && field.id === id) {
                        return false
                    }
                    return true
                })
        }
        const fieldBodyContent = newFields.find((item) => item.id === parentId)
        dispatch({fields: newFields, fieldBodyContent})
    }

    const onClickDelete = (deletedField) => {
        const newFields = fields
            .filter((field) => {
                if (!field.parentId) {
                    return field.id !== deletedField.id
                }
                return field.parentId !== deletedField.id
            })
            .map((field) => {
                if (
                    field.isConditionalRule &&
                    field.dependencies.some((dependencyField) => dependencyField?.uuid?.uuid === deletedField.uuid)
                ) {
                    field.isConditionalRule = false
                    field.dependencies = []
                }
                return field
            })
        const remainingIds = new Set(newFields.map((field) => field.id))
        dispatch({
            fields: newFields,
            activeField: null,
            selectedFieldIds: selectedFieldIds.filter((id) => remainingIds.has(id)),
            groupDrag: null
        })
    }

    const onClickField = (field) => {
        const newPreviousFieldInfo = {
            ...previousFieldInfo,
            width: field.placeInformation.width,
            height: field.placeInformation.height
        }
        if (field.parentId) {
            const fieldBodyContent = fields.find((item) => item.id === field.parentId)
            dispatch({fieldBodyContent, previousFieldInfo: newPreviousFieldInfo})
        } else {
            dispatch({fieldBodyContent: field, previousFieldInfo: newPreviousFieldInfo})
        }
    }

    const validateData = () => {
        const fieldsValidator = fields
            .filter((field) => !field.parentId)
            .map((field) => ({
                id: field.id,
                signerIds: (field.signatureTypes || []).map((signatureType) => signatureType.id)
            }))
        const invalidSignerFieldIds = fieldsValidator
            .filter((field) => isEmpty(field.signerIds))
            .map((field) => field.id)
        if (!isEmpty(invalidSignerFieldIds)) {
            setErrorFieldIds(invalidSignerFieldIds)
            toastError("Please select at least 1 signer")
            return true
        }
        const invalidTextFieldIds = fields
            .filter(
                (field) =>
                    field.type === DigitalDocumentFieldSetUp.TEXT &&
                    field.defaultDataType?.id === DefaultDataType.STUDENT_DATA &&
                    (!field.defaultParticipant || !field.defaultData)
            )
            .map((field) => field.id)
        if (!isEmpty(invalidTextFieldIds)) {
            setErrorFieldIds(invalidTextFieldIds)
            toastError("Please select at participant info in text fields")
            return true
        }
        const invalidSelectFieldIds = fields
            .filter(
                (field) =>
                    field.type === DigitalDocumentFieldSetUp.SELECT &&
                    (isEmpty(field.fieldValues) || field.fieldValues.some((fieldValue) => !fieldValue.option))
            )
            .map((field) => field.id)
        if (!isEmpty(invalidSelectFieldIds)) {
            setErrorFieldIds(invalidSelectFieldIds)
            toastError("Please add at least 1 option and label not empty in select fields")
            return true
        }
        const duplicateOptionSelectFieldIds = fields
            .filter(
                (field) =>
                    field.type === DigitalDocumentFieldSetUp.SELECT &&
                    uniq(field.fieldValues.map((fieldValue) => trim(fieldValue.option))).length !==
                        field.fieldValues.length
            )
            .map((field) => field.id)
        if (!isEmpty(duplicateOptionSelectFieldIds)) {
            setErrorFieldIds(duplicateOptionSelectFieldIds)
            toastError("Options in select field must be different")
            return true
        }
        const invalidConditionalFields = fields
            .filter((field) => {
                if (field.isConditionalRule) {
                    const missingValue = field.dependencies.some((item) => !item.condition || !item.uuid)
                    if (missingValue) {
                        return true
                    }
                    const missingEqualValue = field.dependencies.some(
                        (item) => item.condition.id === DigitalTaskCondition.EQUALS_TO && !item.value
                    )
                    if (missingEqualValue) {
                        return true
                    }
                }
                return false
            })
            .map((field) => field.id)
        if (!isEmpty(invalidConditionalFields)) {
            setErrorFieldIds(invalidConditionalFields)
            toastError("Please select condition rule info")
            return true
        }
        const circularFields = getCircularDependencyFields(fields)
        if (!isEmpty(circularFields)) {
            setErrorFieldIds(circularFields.map((item) => item.id))
            toastError("Circular dependency found")
            return true
        }

        setErrorFieldIds(null)
        return false
    }

    const onClickSave = async () => {
        const isInvalid = validateData()
        if (isInvalid) {
            return
        }
        try {
            dispatch({isSaving: true})
            const dataSubmit = getDataSubmit({fields, taskData, margin, pageRef, participants})
            await taskService.editDigitalDocumentField(dataSubmit)
            if (taskData.subtype === TaskSubtype.LIBRARY && queryParams.id) {
                toastSuccess("Task updated successfully")
                const params = new URLSearchParams(location.search)
                params.delete("configure")
                const newSearch = params.toString()
                const newPath = `${location.pathname}${newSearch ? `?${newSearch}` : ""}`
                history.replace(newPath)
                onCloseDigitalDocSetup()
            } else {
                toastSuccess("Task submitted successfully")
                history.push(`/tasks?tab=${getBackUrlTab({type: taskData.type, subtype: taskData.subtype})}`)
            }
        } catch (error) {
            dispatch({isSaving: false})
            handleError(error)
        } finally {
            dispatch({isSaving: false})
        }
    }

    const onChangeCombinedColor = (sortedSignerIds: string, color: string) => {
        const newCombinedColor = {...combinedColor}
        newCombinedColor[sortedSignerIds] = color
        dispatch({combinedColor: newCombinedColor})
    }

    const onDuplicateField = (field) => {
        const {newFields, newField} = duplicateField(fields, field)
        dispatch({
            fields: newFields,
            activeField: newField,
            fieldBodyContent: newField,
            selectedFieldIds: [newField.id],
            groupDrag: null
        })
    }

    const boundaryWidth = get(bodyRef.current, "offsetWidth", 300) - 32
    const boundaryHeight = get(bodyRef.current, "offsetHeight", 1000) - 32
    boundaryRef.current = {width: boundaryWidth, height: boundaryHeight}
    keyHandlerRef.current = (event) => {
        const target = event.target as HTMLElement | null
        if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) {
            return
        }
        if (event.key === "Escape") {
            dispatch({selectedFieldIds: [], activeField: null, groupDrag: null})
            return
        }
        const step = event.shiftKey ? 10 : 1
        let dx = 0
        let dy = 0
        if (event.key === "ArrowLeft") dx = -step
        else if (event.key === "ArrowRight") dx = step
        else if (event.key === "ArrowUp") dy = -step
        else if (event.key === "ArrowDown") dy = step
        else return
        if (!selectedFieldIdsRef.current.length) return
        event.preventDefault()
        moveSelectedFields(dx, dy)
    }
    if (isEmpty(task)) {
        return <BaseLoading isShow />
    }

    return (
        <div ref={wrapRef} className={styles.wrap}>
            {!isImgsLoaded && <BaseLoading isShow />}
            <div className={styles.headerContainer}>
                <div style={{height: 12}}></div>
                <div className={styles.headerContentWrap}>
                    <div className={styles.headerWrap}>
                        <DefinedUser definedUserTypes={definedUserTypes} />
                        <HeaderBody
                            type={taskData.type}
                            taskId={taskData.id}
                            isDeleted={!!taskData.deletedAt}
                            activeField={activeField}
                            isSaving={isSaving}
                            isEditable={isEditable}
                            onAddField={onAddField}
                            getIconByType={getIconByType}
                            onClickSave={onClickSave}
                        />
                    </div>
                    {isEditable && selectedFieldIds.length > 1 && (
                        <FieldAlignActions count={selectedFieldIds.length} onAlign={onAlignFields} />
                    )}
                    <CreatedInfo data={taskData} userInfoKey="createdByUser" />
                </div>
            </div>
            <div
                ref={bodyRef}
                className={styles.body}
                style={{width: pageRef?.current?.offsetWidth}}
                onMouseDown={onCanvasMouseDown}>
                {fields.map((field) => (
                    <FieldAction
                        key={field.id}
                        field={field}
                        fields={fields}
                        fieldBodyContent={fieldBodyContent}
                        activeField={activeField}
                        boundaryWidth={boundaryWidth}
                        boundaryHeight={boundaryHeight}
                        definedUsers={definedUsers}
                        participants={participants}
                        combinedColor={combinedColor}
                        scaleInfo={scaleInfo}
                        totalPage={task.digitalDocument.pages.length}
                        previousFieldInfo={previousFieldInfo}
                        isEditable={isEditable}
                        errorFieldIds={errorFieldIds}
                        onDragStart={onDragStart}
                        onDrag={onDrag}
                        onDragStop={onDragStop}
                        onResize={onResize}
                        getIconByType={getIconByType}
                        onClickDelete={onClickDelete}
                        setActiveField={setActiveField}
                        onChangeField={onChangeField}
                        onAddNewFieldValue={onAddNewFieldValue}
                        onRemoveFieldValue={onRemoveFieldValue}
                        onClickField={onClickField}
                        onChangeFieldValue={onChangeFieldValue}
                        onChangeCombinedColor={onChangeCombinedColor}
                        onDuplicateField={onDuplicateField}
                        selectedFieldIds={selectedFieldIds}
                        groupDrag={groupDrag}
                        onToggleFieldSelection={onToggleFieldSelection}
                        onSelectOnlyField={onSelectOnlyField}
                    />
                ))}
                {selectionBox && (selectionBox.width > 0 || selectionBox.height > 0) && (
                    <div
                        className={styles.selectionBox}
                        style={{
                            left: selectionBox.left,
                            top: selectionBox.top,
                            width: selectionBox.width,
                            height: selectionBox.height
                        }}
                    />
                )}
                <div className={styles.pageWrap}>
                    {(task.digitalDocument.pages || []).map((page, index) => (
                        <img
                            ref={pageRef}
                            key={index}
                            className={cx(styles.pageImg, {
                                [styles.lastPage]: index === task.digitalDocument.pages.length - 1
                            })}
                            src={page.url}
                            alt=""
                            draggable={false}
                        />
                    ))}
                </div>
            </div>
        </div>
    )
}
